import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMetrics, type KpiCard as KpiCardData, type LeadingIndicator } from "@/hooks/useMetrics";

const KPI_TOOLTIPS: Record<string, string> = {
  cycle_time: "Tiempo promedio desde apertura hasta cierre de una story",
  deploy_frequency: "Stories cerradas por semana (proxy para cadencia de entrega)",
  change_failure_rate: "% de stories que requirieron reapertura tras el cierre",
  rework_rate: "Reaperturas promedio por story cerrada",
  cache_hit_rate: "% de tokens servidos desde caché vs. procesados fresh",
  output_tokens: "Total de tokens generados en el período",
  est_cost: "Costo estimado basado en precios publicados del modelo",
  sessions: "Sesiones de trabajo registradas (eventos de token)",
};

const trendColor: Record<KpiCardData["trend"], string> = {
  good: "text-emerald-500",
  warning: "text-amber-500",
  bad: "text-red-500",
};

function deltaLabel(delta: number, vsPrevPeriod: string): string {
  const sign = delta < 0 ? "↓" : "↑";
  return `${sign} ${Math.abs(delta).toFixed(1)} ${vsPrevPeriod}`;
}

function KpiCardDisplay({ kpi }: { kpi: KpiCardData }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-medium" title={KPI_TOOLTIPS[kpi.id]}>{kpi.label}</CardTitle>
          {kpi.is_proxy && (
            <span
              className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground"
              title={t("dashboard.proxyHint")}
            >
              {t("dashboard.proxyBadge")}
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{kpi.unit}</span>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{Number(kpi.value.toFixed(1))}</div>
        <p className={`text-xs ${trendColor[kpi.trend] ?? "text-muted-foreground"}`}>
          {deltaLabel(kpi.delta, t("dashboard.vsPrevPeriod"))}
        </p>
      </CardContent>
    </Card>
  );
}

function LeadingIndicatorCard({ indicator }: { indicator: LeadingIndicator }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{indicator.label}</CardTitle>
        <span className="text-xs text-muted-foreground">{indicator.unit}</span>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{Number(indicator.value.toFixed(1))}</div>
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
      <h3 className="text-sm font-medium text-foreground mb-1">
        {t("delivery.empty")}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        {t("delivery.emptyDetail")}
      </p>
    </div>
  );
}

interface DeliveryMetricsSectionProps {
  /** Project slug to scope metrics to. Omitted = org scope. */
  projectSlug?: string;
  /** Section heading — shown on the project tab, omitted on the dashboard. */
  title?: string;
}

/**
 * Delivery KPI grid (S16430.6, SD3) — self-fetches via useMetrics, shared
 * by the project metrics tab (projectSlug set) and DashboardPage (org
 * scope, no projectSlug).
 */
export function DeliveryMetricsSection({
  projectSlug,
  title,
}: DeliveryMetricsSectionProps) {
  const { t } = useTranslation();
  const { metrics, loading, error } = useMetrics(projectSlug);

  return (
    <div className="space-y-4">
      {title && <h2 className="text-lg font-semibold">{title}</h2>}

      {error && <p className="text-sm text-destructive">{t(error)}</p>}

      {loading ? (
        <p className="text-muted-foreground">{t("dashboard.loading")}</p>
      ) : (
        !error &&
        metrics &&
        (metrics.has_data ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {metrics.kpis.map((kpi) => (
                <KpiCardDisplay key={kpi.id} kpi={kpi} />
              ))}
            </div>
            {metrics.leading_indicators.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {metrics.leading_indicators.map((ind) => (
                  <LeadingIndicatorCard key={ind.id} indicator={ind} />
                ))}
              </div>
            )}
          </div>
        ) : (
          <EmptyState />
        ))
      )}
    </div>
  );
}
